import { useEffect, useState } from "react";
import { Check, Copy, Download, X } from "lucide-react";
import { getWorkCitation, type CitationStyle } from "./workApi.js";

const styles: Array<{ value: CitationStyle; label: string }> = [
  { value: "gb-t-7714-2015-numeric", label: "GB/T 7714—2015" },
  { value: "apa-7", label: "APA 7" },
  { value: "mla-9", label: "MLA 9" },
  { value: "chicago-author-date", label: "Chicago Author-Date" },
  { value: "vancouver", label: "Vancouver" }
];

function downloadText(filename: string, content: string, type: string) {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return;
    } catch {
      // Browser permission and embedded contexts can reject Clipboard API; use the legacy selection fallback below.
    }
  }
  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  textarea.remove();
  if (!copied) throw new Error("copy unavailable");
}

export function CitationDialog({ workId, title, open, onClose }: { workId: string; title: string; open: boolean; onClose: () => void }) {
  const [style, setStyle] = useState<CitationStyle>("gb-t-7714-2015-numeric");
  const [citation, setCitation] = useState<{ text: string; bibtex: string; ris: string; missingFields: string[]; accessDateRecommended?: boolean } | null>(null);
  const [accessed, setAccessed] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState("");
  const [copyError, setCopyError] = useState("");

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    setError("");
    setCopyError("");
    setCopied("");
    getWorkCitation(workId, style, accessed || undefined).then((value) => {
      if (!active) return;
      setCitation(value);
      if (value.accessDateRecommended && !accessed) setAccessed(new Date().toISOString().slice(0, 10));
    }).catch(() => {
      if (active) setError("引用格式生成失败，请稍后重试。");
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [open, workId, style, accessed]);

  useEffect(() => {
    if (!open) return;
    const close = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    document.addEventListener("keydown", close);
    return () => document.removeEventListener("keydown", close);
  }, [open, onClose]);

  async function copy(kind: "text" | "bibtex" | "ris") {
    const value = citation?.[kind];
    if (!value) return;
    setCopyError("");
    try {
      await copyText(value);
      setCopied(kind);
      window.setTimeout(() => setCopied(""), 1400);
    } catch {
      setCopyError("复制失败，请手动选择引用内容复制。");
    }
  }

  if (!open) return null;
  return (
    <div className="citation-dialog-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="citation-dialog" role="dialog" aria-modal="true" aria-labelledby="citation-dialog-title">
        <header>
          <div><span>引用论文</span><h2 id="citation-dialog-title">{title}</h2></div>
          <button type="button" aria-label="关闭引用弹窗" onClick={onClose}><X size={18} /></button>
        </header>
        <label className="citation-style-select">引用格式
          <select value={style} onChange={(event) => setStyle(event.target.value as CitationStyle)}>
            {styles.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
        </label>
        <div className="citation-preview" aria-live="polite">
          {loading ? "正在生成…" : error || citation?.text || "暂无可生成的引用。"}
        </div>
        {citation?.accessDateRecommended && <label className="citation-access-date">访问日期<input type="date" value={accessed} onChange={(event) => setAccessed(event.target.value)} /><small>仅用于需要访问日期的在线资料，不写入共享论文元数据。</small></label>}
        {citation?.missingFields.length ? <p className="citation-missing">部分字段尚未获取：{citation.missingFields.map((field) => ({ authors: "作者", issuedDate: "发表日期", venue: "期刊/来源", identifierOrUrl: "标识符或链接" }[field] || field)).join("、")}。已按现有信息生成。</p> : null}
        {copyError && <p className="citation-copy-error" role="alert">{copyError}</p>}
        <footer>
          <button className="citation-primary" type="button" onClick={() => void copy("text")} disabled={!citation}>
            {copied === "text" ? <Check size={15} /> : <Copy size={15} />} {copied === "text" ? "已复制" : "复制引用"}
          </button>
          <button type="button" onClick={() => void copy("bibtex")} disabled={!citation}>
            {copied === "bibtex" ? <Check size={15} /> : <Copy size={15} />} {copied === "bibtex" ? "BibTeX 已复制" : "复制 BibTeX"}
          </button>
          <button type="button" onClick={() => citation && downloadText("reference.bib", citation.bibtex, "application/x-bibtex")} disabled={!citation}><Download size={15} /> .bib</button>
          <button type="button" onClick={() => citation && downloadText("reference.ris", citation.ris, "application/x-research-info-systems")} disabled={!citation}><Download size={15} /> .ris</button>
        </footer>
      </section>
    </div>
  );
}
